(() => {
    'use strict';

    const HEADER_SELECTOR = '[data-header]';
    const DESKTOP_QUERY = '(min-width: 1100px)';
    const HOVER_QUERY = '(hover: hover) and (pointer: fine)';
    const DESKTOP_CLOSE_DELAY = 180;

    const focusableSelector = [
        'a[href]',
        'button:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])'
    ].join(',');

    const instances = new WeakMap();

    const getFocusableElements = (container) =>
        [...container.querySelectorAll(focusableSelector)].filter(
            (element) =>
                !element.hasAttribute('inert') &&
                !element.closest('[hidden]') &&
                (
                    element.offsetWidth ||
                    element.offsetHeight ||
                    element.getClientRects().length
                )
        );

    const getControlledElement = (control) => {
        const id = control?.getAttribute('aria-controls');

        return id
            ? document.getElementById(id)
            : null;
    };

    const parseTime = (value) => {
        const trimmed = value.trim();

        if (trimmed.endsWith('ms')) {
            return Number.parseFloat(trimmed) || 0;
        }

        if (trimmed.endsWith('s')) {
            return (Number.parseFloat(trimmed) || 0) * 1000;
        }

        return 0;
    };

    const getTransitionTotal = (element) => {
        if (!element) {
            return 0;
        }

        const styles = window.getComputedStyle(element);

        const durations = styles.transitionDuration
            .split(',')
            .map(parseTime);

        const delays = styles.transitionDelay
            .split(',')
            .map(parseTime);

        const count = Math.max(
            durations.length,
            delays.length
        );

        let longest = 0;

        for (
            let index = 0;
            index < count;
            index += 1
        ) {
            const duration =
                durations[index % durations.length] || 0;

            const delay =
                delays[index % delays.length] || 0;

            longest = Math.max(
                longest,
                duration + delay
            );
        }

        return longest;
    };

    class HeaderController {
        constructor(header) {
            this.header = header;

            this.menuToggle =
                header.querySelector(
                    '.header__menu-toggle'
                );

            this.mobileMenu =
                header.querySelector(
                    '[data-header-mobile-menu]'
                );

            this.mobilePanel =
                header.querySelector(
                    '.header__mobile-panel'
                );

            this.mobileClose =
                header.querySelector(
                    '.header__mobile-close'
                );

            this.mobileBackdrop =
                header.querySelector(
                    '.header__mobile-backdrop'
                );

            this.mobileSubmenuToggles = [
                ...header.querySelectorAll(
                    '.header__mobile-submenu-toggle'
                )
            ];

            this.desktopMenuItems = [
                ...header.querySelectorAll(
                    '[data-header-menu-item]'
                )
            ];

            this.desktopBreakpoint =
                window.matchMedia(
                    DESKTOP_QUERY
                );

            this.hoverCapable =
                window.matchMedia(
                    HOVER_QUERY
                );

            this.abortController =
                new AbortController();

            this.lastFocusedElement = null;
            this.activeMobileSubmenu = null;
            this.activeDesktopMenu = null;

            this.mobileCloseTimer = null;
            this.desktopCloseTimer = null;
            this.desktopHideTimer = null;

            this.handleBreakpointChange =
                this.handleBreakpointChange.bind(this);

            this.init();
        }

        init() {
            const signal =
                this.abortController.signal;

            /*
             * ------------------------------------------------------------
             * Mobile drawer
             * ------------------------------------------------------------
             */

            if (
                this.menuToggle &&
                this.mobileMenu &&
                this.mobilePanel &&
                this.mobileClose &&
                this.mobileBackdrop
            ) {
                this.menuToggle.addEventListener(
                    'click',
                    () => {
                        if (this.isMobileMenuOpen()) {
                            this.closeMobileMenu();
                        } else {
                            this.openMobileMenu();
                        }
                    },
                    { signal }
                );

                this.mobileClose.addEventListener(
                    'click',
                    () => {
                        this.closeMobileMenu();
                    },
                    { signal }
                );

                this.mobileBackdrop.addEventListener(
                    'click',
                    () => {
                        this.closeMobileMenu();
                    },
                    { signal }
                );

                this.mobileMenu.addEventListener(
                    'click',
                    (event) => {
                        this.handleMobileMenuClick(
                            event
                        );
                    },
                    { signal }
                );
            }

            /*
             * ------------------------------------------------------------
             * Desktop mega menus
             *
             * Hover opens the menu.
             * Clicking the parent link is intentionally untouched so it
             * continues navigating to its URL.
             * ------------------------------------------------------------
             */

            this.desktopMenuItems.forEach(
                (item) => {
                    const link =
                        item.querySelector(
                            '[data-header-menu-link]'
                        );

                    const megaMenu =
                        item.querySelector(
                            '[data-header-mega-menu]'
                        );

                    if (
                        !link ||
                        !megaMenu
                    ) {
                        return;
                    }

                    /*
                     * Hidden menus should never expose their links to keyboard
                     * or assistive-technology interaction.
                     */
                    megaMenu.setAttribute(
                        'inert',
                        ''
                    );

                    item.addEventListener(
                        'pointerenter',
                        () => {
                            if (
                                !this.desktopBreakpoint.matches ||
                                !this.hoverCapable.matches
                            ) {
                                return;
                            }

                            this.cancelDesktopClose();

                            this.openDesktopMenu(
                                item,
                                link,
                                megaMenu
                            );
                        },
                        { signal }
                    );

                    item.addEventListener(
                        'pointerleave',
                        () => {
                            if (
                                !this.desktopBreakpoint.matches ||
                                !this.hoverCapable.matches
                            ) {
                                return;
                            }

                            this.scheduleDesktopClose(
                                item
                            );
                        },
                        { signal }
                    );
                }
            );

            /*
             * Keyboard users get the same mega menu when focus reaches
             * a top-level parent link.
             */
            this.header.addEventListener(
                'focusin',
                (event) => {
                    this.handleHeaderFocusIn(
                        event
                    );
                },
                { signal }
            );

            this.header.addEventListener(
                'focusout',
                (event) => {
                    this.handleHeaderFocusOut(
                        event
                    );
                },
                { signal }
            );

            document.addEventListener(
                'keydown',
                (event) => {
                    this.handleKeydown(
                        event
                    );
                },
                { signal }
            );

            document.addEventListener(
                'click',
                (event) => {
                    this.handleDocumentClick(
                        event
                    );
                },
                { signal }
            );

            this.desktopBreakpoint
                .addEventListener(
                    'change',
                    this.handleBreakpointChange
                );
        }

        /* ==============================================================
           Mobile drawer
           ============================================================== */

        isMobileMenuOpen() {
            return (
                this.menuToggle?.getAttribute(
                    'aria-expanded'
                ) === 'true'
            );
        }

        setControlLabel(
            control,
            expanded
        ) {
            if (!control) {
                return;
            }

            const label =
                expanded
                    ? control.dataset.closeLabel
                    : control.dataset.openLabel;

            if (label) {
                control.setAttribute(
                    'aria-label',
                    label
                );
            }
        }

        openMobileMenu() {
            if (
                !this.menuToggle ||
                !this.mobileMenu ||
                !this.mobileClose ||
                this.isMobileMenuOpen() ||
                this.desktopBreakpoint.matches
            ) {
                return;
            }

            if (this.mobileCloseTimer) {
                window.clearTimeout(
                    this.mobileCloseTimer
                );

                this.mobileCloseTimer = null;
            }

            this.closeDesktopMenu({
                immediate: true
            });

            this.lastFocusedElement =
                document.activeElement;

            this.mobileMenu.hidden = false;

            this.mobileMenu.removeAttribute(
                'inert'
            );

            this.menuToggle.setAttribute(
                'aria-expanded',
                'true'
            );

            this.menuToggle.setAttribute(
                'aria-label',
                'Close menu'
            );

            document.documentElement
                .classList.add(
                    'header-menu-open'
                );

            requestAnimationFrame(() => {
                this.mobileMenu.classList.add(
                    'is-open'
                );

                requestAnimationFrame(() => {
                    this.mobileClose.focus();
                });
            });
        }

        closeMobileMenu({
            restoreFocus = true,
            immediate = false
        } = {}) {
            if (
                !this.mobileMenu ||
                !this.menuToggle
            ) {
                return;
            }

            if (
                !this.isMobileMenuOpen() &&
                this.mobileMenu.hidden
            ) {
                return;
            }

            this.closeMobileSubmenu({
                restoreFocus: false
            });

            this.menuToggle.setAttribute(
                'aria-expanded',
                'false'
            );

            this.menuToggle.setAttribute(
                'aria-label',
                'Open menu'
            );

            this.mobileMenu.classList.remove(
                'is-open'
            );

            this.mobileMenu.setAttribute(
                'inert',
                ''
            );

            document.documentElement
                .classList.remove(
                    'header-menu-open'
                );

            if (
                restoreFocus &&
                this.lastFocusedElement &&
                document.contains(
                    this.lastFocusedElement
                )
            ) {
                requestAnimationFrame(() => {
                    this.lastFocusedElement.focus();
                });
            }

            this.lastFocusedElement = null;

            if (this.mobileCloseTimer) {
                window.clearTimeout(
                    this.mobileCloseTimer
                );
            }

            const delay =
                immediate
                    ? 0
                    : Math.max(
                        getTransitionTotal(
                            this.mobilePanel
                        ),
                        getTransitionTotal(
                            this.mobileBackdrop
                        )
                    );

            this.mobileCloseTimer =
                window.setTimeout(
                    () => {
                        if (
                            !this.isMobileMenuOpen()
                        ) {
                            this.mobileMenu.hidden =
                                true;
                        }

                        this.mobileCloseTimer =
                            null;
                    },
                    delay + 20
                );
        }

        /* ==============================================================
           Desktop mega menu
           ============================================================== */

        openDesktopMenu(
            item,
            link,
            megaMenu
        ) {
            if (
                !this.desktopBreakpoint.matches ||
                !item ||
                !link ||
                !megaMenu
            ) {
                return;
            }

            this.cancelDesktopClose();

            if (this.desktopHideTimer) {
                window.clearTimeout(
                    this.desktopHideTimer
                );

                this.desktopHideTimer = null;
            }

            /*
             * Already active: make sure it has not been hidden during
             * an interrupted close transition.
             */
            if (
                this.activeDesktopMenu?.item ===
                item
            ) {
                if (megaMenu.hidden) {
                    megaMenu.hidden = false;

                    megaMenu.removeAttribute(
                        'inert'
                    );

                    requestAnimationFrame(() => {
                        megaMenu.classList.add(
                            'is-open'
                        );
                    });
                }

                return;
            }

            /*
             * Only one mega menu should be active at a time.
             */
            if (this.activeDesktopMenu) {
                this.closeDesktopMenu({
                    immediate: true
                });
            }

            link.setAttribute(
                'aria-expanded',
                'true'
            );

            item.setAttribute(
                'data-mega-menu-open',
                ''
            );

            megaMenu.hidden = false;

            megaMenu.removeAttribute(
                'inert'
            );

            this.activeDesktopMenu = {
                item,
                link,
                megaMenu
            };

            requestAnimationFrame(() => {
                if (
                    this.activeDesktopMenu?.item ===
                    item
                ) {
                    megaMenu.classList.add(
                        'is-open'
                    );
                }
            });
        }

        scheduleDesktopClose(item) {
            if (
                !this.activeDesktopMenu ||
                this.activeDesktopMenu.item !==
                item
            ) {
                return;
            }

            this.cancelDesktopClose();

            this.desktopCloseTimer =
                window.setTimeout(
                    () => {
                        this.closeDesktopMenu();
                    },
                    DESKTOP_CLOSE_DELAY
                );
        }

        cancelDesktopClose() {
            if (
                !this.desktopCloseTimer
            ) {
                return;
            }

            window.clearTimeout(
                this.desktopCloseTimer
            );

            this.desktopCloseTimer = null;
        }

        closeDesktopMenu({
            restoreFocus = false,
            immediate = false
        } = {}) {
            this.cancelDesktopClose();

            if (
                !this.activeDesktopMenu
            ) {
                return;
            }

            const {
                item,
                link,
                megaMenu
            } = this.activeDesktopMenu;

            /*
             * Clear the active reference before beginning the closing
             * transition so another menu can open immediately.
             */
            this.activeDesktopMenu = null;

            link.setAttribute(
                'aria-expanded',
                'false'
            );

            item.removeAttribute(
                'data-mega-menu-open'
            );

            megaMenu.classList.remove(
                'is-open'
            );

            megaMenu.setAttribute(
                'inert',
                ''
            );

            if (this.desktopHideTimer) {
                window.clearTimeout(
                    this.desktopHideTimer
                );
            }

            const delay =
                immediate
                    ? 0
                    : getTransitionTotal(
                        megaMenu
                    );

            this.desktopHideTimer =
                window.setTimeout(
                    () => {
                        if (
                            !megaMenu.classList
                                .contains(
                                    'is-open'
                                )
                        ) {
                            megaMenu.hidden = true;
                        }

                        this.desktopHideTimer =
                            null;
                    },
                    delay + 20
                );

            if (
                restoreFocus &&
                document.contains(link)
            ) {
                requestAnimationFrame(() => {
                    link.focus();
                });
            }
        }

        /* ==============================================================
           Mobile nested menus
           ============================================================== */

        openMobileSubmenu(toggle) {
            const submenu =
                getControlledElement(
                    toggle
                );

            if (!submenu) {
                return;
            }

            if (
                this.activeMobileSubmenu &&
                this.activeMobileSubmenu.toggle !==
                toggle
            ) {
                this.closeMobileSubmenu({
                    restoreFocus: false
                });
            }

            toggle.setAttribute(
                'aria-expanded',
                'true'
            );

            this.setControlLabel(
                toggle,
                true
            );

            submenu.hidden = false;

            this.activeMobileSubmenu = {
                toggle,
                submenu
            };
        }

        closeMobileSubmenu({
            restoreFocus = false
        } = {}) {
            if (
                !this.activeMobileSubmenu
            ) {
                return;
            }

            const {
                toggle,
                submenu
            } = this.activeMobileSubmenu;

            toggle.setAttribute(
                'aria-expanded',
                'false'
            );

            this.setControlLabel(
                toggle,
                false
            );

            submenu.hidden = true;

            this.activeMobileSubmenu = null;

            if (
                restoreFocus &&
                document.contains(toggle)
            ) {
                toggle.focus();
            }
        }

        handleMobileMenuClick(event) {
            const toggle =
                event.target.closest(
                    '.header__mobile-submenu-toggle'
                );

            if (
                toggle &&
                this.mobileMenu?.contains(
                    toggle
                )
            ) {
                event.preventDefault();

                const expanded =
                    toggle.getAttribute(
                        'aria-expanded'
                    ) === 'true';

                if (expanded) {
                    this.closeMobileSubmenu({
                        restoreFocus: false
                    });
                } else {
                    this.openMobileSubmenu(
                        toggle
                    );
                }

                return;
            }

            const link =
                event.target.closest(
                    'a[href]'
                );

            if (
                link &&
                this.mobileMenu?.contains(
                    link
                )
            ) {
                this.closeMobileMenu({
                    restoreFocus: false
                });
            }
        }

        /* ==============================================================
           Keyboard navigation
           ============================================================== */

        handleHeaderFocusIn(event) {
            if (
                !this.desktopBreakpoint.matches
            ) {
                return;
            }

            const link =
                event.target.closest(
                    '[data-header-menu-link]'
                );

            if (
                link &&
                this.header.contains(link)
            ) {
                const item =
                    link.closest(
                        '[data-header-menu-item]'
                    );

                const megaMenu =
                    item?.querySelector(
                        '[data-header-mega-menu]'
                    );

                if (
                    item &&
                    megaMenu
                ) {
                    this.cancelDesktopClose();

                    this.openDesktopMenu(
                        item,
                        link,
                        megaMenu
                    );

                    return;
                }
            }

            /*
             * Focus moved somewhere inside the currently-open mega menu.
             * Cancel any pending pointer-based close.
             */
            if (
                this.activeDesktopMenu &&
                this.activeDesktopMenu.item
                    .contains(
                        event.target
                    )
            ) {
                this.cancelDesktopClose();
            }
        }

        handleHeaderFocusOut(event) {
            if (
                !this.desktopBreakpoint.matches ||
                !this.activeDesktopMenu
            ) {
                return;
            }

            const nextFocused =
                event.relatedTarget;

            if (
                nextFocused instanceof Node &&
                this.activeDesktopMenu.item
                    .contains(
                        nextFocused
                    )
            ) {
                return;
            }

            /*
             * Wait until the next element actually receives focus before
             * deciding whether the current mega menu should close.
             */
            window.setTimeout(
                () => {
                    if (
                        !this.activeDesktopMenu
                    ) {
                        return;
                    }

                    if (
                        this.activeDesktopMenu.item
                            .contains(
                                document.activeElement
                            )
                    ) {
                        return;
                    }

                    this.closeDesktopMenu();
                },
                0
            );
        }

        handleDocumentClick(event) {
            if (
                !this.activeDesktopMenu
            ) {
                return;
            }

            if (
                this.activeDesktopMenu.item
                    .contains(
                        event.target
                    )
            ) {
                /*
                 * This deliberately does NOT prevent the parent navigation
                 * link from navigating.
                 */
                return;
            }

            this.closeDesktopMenu({
                immediate: true
            });
        }

        handleKeydown(event) {
            if (
                event.key === 'Escape'
            ) {
                /*
                 * Mobile Escape:
                 * close the nested submenu first, then the drawer.
                 */
                if (
                    this.isMobileMenuOpen()
                ) {
                    event.preventDefault();

                    if (
                        this.activeMobileSubmenu
                    ) {
                        this.closeMobileSubmenu({
                            restoreFocus: true
                        });
                    } else {
                        this.closeMobileMenu();
                    }

                    return;
                }

                /*
                 * Desktop Escape:
                 * close the mega menu and return focus to its parent link.
                 */
                if (
                    this.activeDesktopMenu
                ) {
                    event.preventDefault();

                    this.closeDesktopMenu({
                        restoreFocus: true,
                        immediate: true
                    });
                }

                return;
            }

            /*
             * Focus trapping only applies to the modal mobile drawer.
             */
            if (
                event.key !== 'Tab' ||
                !this.isMobileMenuOpen() ||
                !this.mobilePanel
            ) {
                return;
            }

            const focusableElements =
                getFocusableElements(
                    this.mobilePanel
                );

            if (
                !focusableElements.length
            ) {
                event.preventDefault();

                this.mobileClose?.focus();

                return;
            }

            const firstElement =
                focusableElements[0];

            const lastElement =
                focusableElements[
                focusableElements.length - 1
                ];

            if (
                !this.mobilePanel.contains(
                    document.activeElement
                )
            ) {
                event.preventDefault();

                firstElement.focus();

                return;
            }

            if (
                event.shiftKey &&
                document.activeElement ===
                firstElement
            ) {
                event.preventDefault();

                lastElement.focus();

                return;
            }

            if (
                !event.shiftKey &&
                document.activeElement ===
                lastElement
            ) {
                event.preventDefault();

                firstElement.focus();
            }
        }

        /* ==============================================================
           Responsive lifecycle
           ============================================================== */

        handleBreakpointChange(event) {
            if (event.matches) {
                /*
                 * We entered desktop.
                 */
                this.closeMobileMenu({
                    restoreFocus: false,
                    immediate: true
                });
            } else {
                /*
                 * We entered tablet/mobile.
                 */
                this.closeDesktopMenu({
                    immediate: true
                });
            }
        }

        /* ==============================================================
           Theme Editor helpers
           ============================================================== */

        openMegaMenuForEditorTarget(
            target
        ) {
            if (
                !this.desktopBreakpoint.matches ||
                !(target instanceof Element)
            ) {
                return;
            }

            const megaMenu =
                target.closest(
                    '[data-header-mega-menu]'
                );

            if (!megaMenu) {
                return;
            }

            const item =
                megaMenu.closest(
                    '[data-header-menu-item]'
                );

            const link =
                item?.querySelector(
                    '[data-header-menu-link]'
                );

            if (
                item &&
                link
            ) {
                this.openDesktopMenu(
                    item,
                    link,
                    megaMenu
                );
            }
        }

        closeMegaMenuForEditorTarget(
            target
        ) {
            if (
                !(target instanceof Element) ||
                !this.activeDesktopMenu
            ) {
                return;
            }

            const megaMenu =
                target.closest(
                    '[data-header-mega-menu]'
                );

            if (
                megaMenu &&
                this.activeDesktopMenu.megaMenu ===
                megaMenu
            ) {
                this.closeDesktopMenu({
                    immediate: true
                });
            }
        }

        /* ==============================================================
           Cleanup
           ============================================================== */

        destroy() {
            this.closeDesktopMenu({
                immediate: true
            });

            this.closeMobileSubmenu({
                restoreFocus: false
            });

            this.closeMobileMenu({
                restoreFocus: false,
                immediate: true
            });

            if (
                this.mobileCloseTimer
            ) {
                window.clearTimeout(
                    this.mobileCloseTimer
                );

                this.mobileCloseTimer = null;
            }

            if (
                this.desktopCloseTimer
            ) {
                window.clearTimeout(
                    this.desktopCloseTimer
                );

                this.desktopCloseTimer = null;
            }

            if (
                this.desktopHideTimer
            ) {
                window.clearTimeout(
                    this.desktopHideTimer
                );

                this.desktopHideTimer = null;
            }

            this.desktopBreakpoint
                .removeEventListener(
                    'change',
                    this.handleBreakpointChange
                );

            document.documentElement
                .classList.remove(
                    'header-menu-open'
                );

            this.abortController.abort();
        }
    }

    /* ================================================================
       Section initialization
       ================================================================ */

    const getHeaders = (
        root = document
    ) => {
        const headers = [];

        if (
            root instanceof Element &&
            root.matches(
                HEADER_SELECTOR
            )
        ) {
            headers.push(root);
        }

        if (
            root instanceof Document ||
            root instanceof DocumentFragment ||
            root instanceof Element
        ) {
            headers.push(
                ...root.querySelectorAll(
                    HEADER_SELECTOR
                )
            );
        }

        return headers;
    };

    const initializeHeaders = (
        root = document
    ) => {
        getHeaders(root).forEach(
            (header) => {
                instances
                    .get(header)
                    ?.destroy();

                const instance =
                    new HeaderController(
                        header
                    );

                instances.set(
                    header,
                    instance
                );
            }
        );
    };

    const destroyHeaders = (
        root
    ) => {
        getHeaders(root).forEach(
            (header) => {
                const instance =
                    instances.get(
                        header
                    );

                if (!instance) {
                    return;
                }

                instance.destroy();

                instances.delete(
                    header
                );
            }
        );
    };

    const getHeaderInstanceForTarget = (
        target
    ) => {
        if (
            !(target instanceof Element)
        ) {
            return null;
        }

        const header =
            target.closest(
                HEADER_SELECTOR
            );

        return header
            ? instances.get(header) || null
            : null;
    };

    /* ================================================================
       Initial page load
       ================================================================ */

    if (
        document.readyState ===
        'loading'
    ) {
        document.addEventListener(
            'DOMContentLoaded',
            () => {
                initializeHeaders(
                    document
                );
            },
            {
                once: true
            }
        );
    } else {
        initializeHeaders(
            document
        );
    }

    /* ================================================================
       Shopify Theme Editor lifecycle
       ================================================================ */

    document.addEventListener(
        'shopify:section:load',
        (event) => {
            initializeHeaders(
                event.target
            );
        }
    );

    document.addEventListener(
        'shopify:section:unload',
        (event) => {
            destroyHeaders(
                event.target
            );
        }
    );

    document.addEventListener(
        'shopify:block:select',
        (event) => {
            getHeaderInstanceForTarget(
                event.target
            )?.openMegaMenuForEditorTarget(
                event.target
            );
        }
    );

    document.addEventListener(
        'shopify:block:deselect',
        (event) => {
            getHeaderInstanceForTarget(
                event.target
            )?.closeMegaMenuForEditorTarget(
                event.target
            );
        }
    );
})();